/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, zip, of, fromEvent } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isNil } from 'lodash';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import {
    ShutdownReason,
    UIShutdownReason,
    NativeShutdownReason,
    ApplicationLifetimeEventType,
    RegisteredWorkHandle
} from './application-lifetime';

@Injectable({
    providedIn: 'root'
})
export class ApplicationLifetimeService {
    private static RegisteredApplicationLifetimeHandle = class implements RegisteredWorkHandle {
        private static nextHandleId = 0;
        private readonly handleId: number;

        constructor(private parent: ApplicationLifetimeService) {
            this.handleId = ApplicationLifetimeService.RegisteredApplicationLifetimeHandle.nextHandleId++;
        }

        getId(): number {
            return this.handleId;
        }

        remove() {
            this.parent.removeWork(this.handleId);
        }
    };

    private logger: Logger;
    private isShuttingDown = false;
    private onShutdownRequestedFunctions: Map<number, () => Observable<boolean>>;
    private onShutdownFunctions: Map<number, () => Observable<void>>;

    private systemShutdownReasons = [
        NativeShutdownReason.UserClosedWindow,
        NativeShutdownReason.UserQuitWithShortcut,
        NativeShutdownReason.UserLoggedOut,
        NativeShutdownReason.Relaunch
    ];

    private unInterruptableShutdownReasons = [
        NativeShutdownReason.UserLoggedOut,
        UIShutdownReason.IpcRequestedExit,
        NativeShutdownReason.SelfUpdate,
        NativeShutdownReason.Relaunch,
        NativeShutdownReason.BrowserExit,
        NativeShutdownReason.ForceShutdown
    ];

    get isAppShuttingDown(): boolean {
        return this.isShuttingDown;
    }

    constructor(
        loggingService: LoggingService,
        private bridgeService: BridgeService
    ) {
        this.logger = loggingService.getLogger('libraries/application-lifetime.service');
        this.logger.info('applicationLifetimeService created');

        this.onShutdownRequestedFunctions = new Map<number, () => Observable<boolean>>();
        this.onShutdownFunctions = new Map<number, () => Observable<void>>();

        bridgeService
            .executeCommand(Commands.Common.Applicationinfo.RegisterAppLifetimeEventsCallback)
            .subscribe((response: Types.Common.Applicationinfo.ApplicationLifetimeEvent) => {
                this.handleBridgeEvent(response);
            });

        // WAR: per http://nvbugs/200470554
        fromEvent(window, 'unload').subscribe(x => {});
    }

    shutdownApplication(reason: ShutdownReason): void {
        if (this.systemShutdownReasons.some(x => x === reason)) {
            this.logger.error('Cannot supply a system reason for shutting down from the application layer');
        } else {
            this.performShutdown(reason);
        }
    }

    addWorkOnShutdownRequested(func: () => Observable<boolean>): RegisteredWorkHandle {
        return this.addWork(this.onShutdownRequestedFunctions, func);
    }

    addWorkOnShutdown(func: () => Observable<void>): RegisteredWorkHandle {
        return this.addWork(this.onShutdownFunctions, func);
    }

    private removeWork(id: number): void {
        if (this.onShutdownFunctions.has(id)) {
            this.onShutdownFunctions.delete(id);
        } else if (this.onShutdownRequestedFunctions.has(id)) {
            this.onShutdownRequestedFunctions.delete(id);
        } else {
            const message = `Id (${id}) not contained in any registered work map, cannot remove`;
            this.logger.error(message);
            throw new Error(message);
        }
    }

    private enumHasValue<EnumType extends object>(enumObj: EnumType, value: any) {
        return Object.values(enumObj).includes(value);
    }

    private handleBridgeEvent(event: Types.Common.Applicationinfo.ApplicationLifetimeEvent): void {
        if (isNil(event) || isNil(event.type)) {
            this.logger.error('Nil event or event.type raised from bridge: ', event);
            return;
        }

        if (!this.enumHasValue(ApplicationLifetimeEventType, event.type)) {
            this.logger.info('Event type', event.type, 'not recognized, not handling');
            return;
        }

        if (
            !this.enumHasValue(UIShutdownReason, event.reason) &&
            !this.enumHasValue(NativeShutdownReason, event.reason)
        ) {
            this.logger.info('Shutdown reason', event.reason, 'not recognized, not handling');
            return;
        }
        this.performShutdown(event.reason);
    }

    private isShutdownReasonInterruptable(reason: ShutdownReason): boolean {
        return this.unInterruptableShutdownReasons.indexOf(reason) === -1;
    }

    private addWork<T>(workStore: Map<number, T>, func: T): RegisteredWorkHandle {
        const handle = new ApplicationLifetimeService.RegisteredApplicationLifetimeHandle(this);
        const id = handle.getId();
        if (this.onShutdownFunctions.has(id) || this.onShutdownRequestedFunctions.has(id)) {
            throw new Error('Dupliate work id found');
        }
        workStore.set(id, func);
        return handle;
    }

    private closeWindowIfNeeded(reason: ShutdownReason): void {
        this.isShuttingDown = false;

        // Do not close window on BrowserExit to allow navigation to another page
        if (reason !== NativeShutdownReason.BrowserExit) {
            this.bridgeService.executeCommand(Commands.Common.Windowcontrol.Close).subscribe(() => {});
        }
    }

    private combineAndExecuteFunctions<T>(workStore: Map<number, () => Observable<T>>): Observable<T[]> {
        const observables: Observable<T>[] = [];
        workStore.forEach((element: () => Observable<T>) => {
            // add obs and handle error to ensure execution of all functions
            let observable: Observable<T>;
            try {
                observable = element().pipe(
                    catchError(err => {
                        this.logger.error('Failed to execute scheduled func: ', err);
                        return of(undefined as T);
                    })
                );
            } catch (error) {
                this.logger.error(`Caught error ${error} from registered handler, not blocking shutdown`);
                observable = of(undefined as T);
            }
            observables.push(observable);
        });
        return zip(...observables);
    }

    private performShutdown(reason: ShutdownReason): void {
        if (this.isShuttingDown) {
            this.logger.info('performShutdown already called, ignoring');
            return;
        }

        this.isShuttingDown = true;
        if (this.onShutdownRequestedFunctions.size === 0 && this.onShutdownFunctions.size === 0) {
            this.logger.info('no pre-shutdown work, closing immediately');
            this.closeWindowIfNeeded(reason);
            return;
        }

        const canInterruptShutdown = this.isShutdownReasonInterruptable(reason);
        this.logger.info(
            'starting pre-shutdown promises. shutdown ',
            canInterruptShutdown ? 'is' : "isn't",
            ' interruptable'
        );

        const performShutdownObs = new Observable(observer => {
            if (canInterruptShutdown && this.onShutdownRequestedFunctions.size > 0) {
                const combined = this.combineAndExecuteFunctions(this.onShutdownRequestedFunctions);
                combined.subscribe({
                    next: values => {
                        observer.next(values.some(x => x === true));
                        observer.complete();
                    },
                    error: x => {
                        /* in case of error simply return with false
                        and continue with un-intrupteed shutdown*/
                        this.logger.error('OnShutdown work, error: ', x);
                        observer.next(false);
                        observer.complete();
                    }
                });
            } else {
                observer.next(false);
                observer.complete();
            }
        });

        performShutdownObs.subscribe({
            next: interruptShutdown => {
                if (interruptShutdown) {
                    this.logger.info('shutdown was interrupted during onShutdownRequested');
                    this.isShuttingDown = false;
                } else if (this.onShutdownFunctions.size === 0) {
                    this.logger.info('no OnShutdown work, closing');
                    this.closeWindowIfNeeded(reason);
                } else {
                    const shutDownObservers = this.combineAndExecuteFunctions(this.onShutdownFunctions);
                    shutDownObservers.subscribe({
                        next: function (x) {
                            // Once all shutdown task executed
                            this.logger.info('OnShutdown work completed, closing');
                            this.closeWindowIfNeeded(reason);
                        }.bind(this),
                        error: function (x) {
                            this.logger.error('OnShutdown work, error: ', x);
                            // in case of any failure continue with window clousre.
                            this.closeWindowIfNeeded(reason);
                        }.bind(this)
                    });
                }
            }
        });
    }
}
